import { allPlanningCategories, PlanningCategory } from './PlanningCategory'
import { PlanningClassification } from './PlanningClassification'

const unique = <T extends string>(values: readonly T[]) => Array.from(new Set(values))

const withCategories = (...groups: PlanningCategory[][]): PlanningCategory[] =>
  unique(groups.flat()).filter(category => allPlanningCategories.includes(category))

const buildingCategories: PlanningCategory[] = [
  'conversions',
  'extensions',
  'new-build',
  'exterior-finish',
  'balconies',
  'structural-alterations',
  'roofing',
  'internal-refurbishments',
  'glazing',
]

const siteCategories: PlanningCategory[] = [
  'groundworks',
  'landscaping',
  'drainage',
  'fencing-boundaries',
  'sewage-treatment',
]

const renewablesCategories: PlanningCategory[] = [
  'renewables-solar',
  'renewables-ev-charging',
  'renewables-heat-pump',
  'renewables-battery-storage',
]

const homeCategories = withCategories(buildingCategories, siteCategories, renewablesCategories, [
  'kitchens',
  'bathrooms',
  'laundry-facilities',
  'lighting',
  'swimming-pools',
])

const institutionalCategories = withCategories(
  buildingCategories,
  siteCategories,
  renewablesCategories,
  ['kitchens', 'bathrooms', 'changing-rooms', 'laundry-facilities', 'lighting'],
)

const commercialCategories = withCategories(
  buildingCategories,
  siteCategories,
  renewablesCategories,
  ['kitchens', 'bathrooms', 'changing-rooms', 'laundry-facilities', 'lighting', 'shop-fronts'],
)

const leisureCategories = withCategories(commercialCategories, [
  'indoor-play-areas',
  'outdoor-play-areas',
  'swimming-pools',
  'skate-parks',
  'sports-fields',
  'sports-courts',
  'pavilions',
])

const outdoorCategories = withCategories(siteCategories, renewablesCategories, [
  'lighting',
  'outdoor-play-areas',
  'swimming-pools',
  'skate-parks',
  'sports-fields',
  'sports-courts',
  'pavilions',
])

const industrialCategories = withCategories(
  buildingCategories,
  siteCategories,
  renewablesCategories,
  ['changing-rooms', 'laundry-facilities', 'lighting'],
)

const farmBuildingCategories = buildingCategories.filter(category => category !== 'conversions')

const farmCategories = withCategories(
  siteCategories,
  renewablesCategories,
  farmBuildingCategories,
  ['new-build', 'lighting'],
)

export const categoriesByClassification: Record<PlanningClassification, PlanningCategory[]> = {
  houses: homeCategories,
  'flats-apartments': homeCategories,
  hmos: homeCategories,
  schools: institutionalCategories,
  'training-centres': institutionalCategories,
  colleges: institutionalCategories,
  universities: institutionalCategories,
  'kindergartens-nurseries-creche': institutionalCategories,
  'community-centres': institutionalCategories,
  libraries: institutionalCategories,
  'civic-government-buildings': institutionalCategories,
  'religious-buildings': institutionalCategories,
  'emergency-justice-buildings': institutionalCategories,
  museums: institutionalCategories,
  galleries: institutionalCategories,
  'public-toilets': withCategories(siteCategories, ['lighting']),
  hospitals: institutionalCategories,
  'health-clinics-surgeries': institutionalCategories,
  'dental-clinics-surgeries': institutionalCategories,
  'care-homes': institutionalCategories,
  'medical-research-facilities': institutionalCategories,
  'veterinary-clinics': institutionalCategories,
  pharmacies: commercialCategories,
  shops: commercialCategories,
  'shopping-centres': commercialCategories,
  supermarkets: commercialCategories,
  offices: commercialCategories,
  'banks-building-societies': commercialCategories,
  showrooms: commercialCategories,
  hotels: leisureCategories,
  'guest-houses': leisureCategories,
  restaurants: leisureCategories,
  cafes: leisureCategories,
  'fast-food': leisureCategories,
  'pubs-bars': leisureCategories,
  'night-clubs': leisureCategories,
  gyms: leisureCategories,
  'leisure-centres': leisureCategories,
  'salons-barbers': commercialCategories,
  'games-venues': leisureCategories,
  'conference-exhibition-venues': leisureCategories,
  stadiums: leisureCategories,
  'outdoor-recreational-areas': outdoorCategories,
  'camp-sites-holiday-villages': withCategories(outdoorCategories, buildingCategories, [
    'kitchens',
    'bathrooms',
    'laundry-facilities',
  ]),
  farms: farmCategories,
  'animal-buildings': industrialCategories,
  'warehouses-storage-buildings': industrialCategories,
  workshops: industrialCategories,
  factories: industrialCategories,
  'industrial-units': industrialCategories,
  'distribution-depots': industrialCategories,
  'vehicle-maintenance': industrialCategories,
  'data-centres': industrialCategories,
  'cold-stores-freezing-plants': industrialCategories,
}

export const getPlanningCategoriesForClassifications = (
  planningClassifications: readonly PlanningClassification[],
): PlanningCategory[] =>
  unique(
    planningClassifications.flatMap(classification => categoriesByClassification[classification]),
  )

export const filterPlanningCategoriesForClassifications = ({
  planningCategories,
  planningClassifications,
}: {
  planningCategories: readonly PlanningCategory[]
  planningClassifications: readonly PlanningClassification[]
}): PlanningCategory[] => {
  const allowedCategories = new Set(
    getPlanningCategoriesForClassifications(planningClassifications),
  )
  return planningCategories.filter(planningCategory => allowedCategories.has(planningCategory))
}
